import { post, isSuccess, extractMessage } from '../../services/api';

function toNumber(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function toNullableNumber(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function pickCoordinate(item, keys) {
  for (const key of keys) {
    const value = toNullableNumber(item?.[key]);
    if (value !== null) return value;
  }

  const location = item?.location || item?.geo || null;
  if (location && typeof location === 'object') {
    for (const key of keys) {
      const value = toNullableNumber(location?.[key]);
      if (value !== null) return value;
    }
  }

  const coords = item?.coordinates || item?.coords || item?.point || '';
  if (typeof coords === 'string' && coords.includes(',')) {
    const [first, second] = coords.split(',').map((part) => toNullableNumber(String(part).trim()));
    if (keys.includes('latitude') && first !== null) return first;
    if (keys.includes('longitude') && second !== null) return second;
  }

  if (Array.isArray(coords) && coords.length >= 2) {
    const first = toNullableNumber(coords[0]);
    const second = toNullableNumber(coords[1]);
    if (keys.includes('latitude') && first !== null) return first;
    if (keys.includes('longitude') && second !== null) return second;
  }

  return null;
}

function normalizeLatitude(value) {
  const numberValue = toNullableNumber(value);
  if (numberValue === null) return null;
  return Math.min(Math.max(numberValue, -85.05112878), 85.05112878);
}

function normalizeLongitude(value) {
  const numberValue = toNullableNumber(value);
  if (numberValue === null) return null;

  let next = numberValue;
  while (next < -180) next += 360;
  while (next > 180) next -= 360;
  return next;
}

function parseGallery(value) {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }

  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed.filter(Boolean);
      }
    } catch (error) {
      return value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
    }
  }

  return [];
}

function normalizeGalleryItems(items) {
  return (Array.isArray(items) ? items : []).map((item, index) => ({
    id: toNumber(item?.id || index + 1, index + 1),
    club_id: toNumber(item?.club_id || 0, 0),
    image_url: item?.image_url || item?.url || '',
    alt_text: item?.alt_text || '',
    sort_order: toNumber(item?.sort_order || index, index),
    is_cover: toNumber(item?.is_cover || 0, 0),
    is_active: item?.is_active === undefined ? 1 : toNumber(item?.is_active || 0, 0),
  })).filter((item) => item.image_url);
}

function normalizeClub(item) {
  const latitude = normalizeLatitude(pickCoordinate(item, ['latitude', 'lat', 'map_latitude', 'geo_latitude']));
  const longitude = normalizeLongitude(pickCoordinate(item, ['longitude', 'lng', 'lon', 'map_longitude', 'geo_longitude']));
  const rating = toNullableNumber(item?.rating);
  const reviewsCount = toNumber(item?.reviews_count || item?.reviewsCount || 0, 0);
  const minPriceFrom = toNullableNumber(item?.min_price_from || item?.minPriceFrom);

  const galleryItems = normalizeGalleryItems(item?.gallery_images || item?.gallery || []);
  const galleryUrls = parseGallery(item?.gallery_json || item?.images_json || item?.image_urls);
  const mergedGallery = [
    ...galleryItems.map((image) => image.image_url),
    ...galleryUrls,
    item?.cover_image_url || '',
    item?.image_url || '',
  ].filter(Boolean);

  const uniqueGallery = Array.from(new Set(mergedGallery));
  const coverImage = item?.cover_image_url || uniqueGallery[0] || item?.image_url || '';

  return {
    ...item,
    id: toNumber(item?.id || 0, 0),
    is_active: toNumber(item?.is_active || 0, 0),
    latitude,
    longitude,
    rating,
    reviews_count: reviewsCount,
    min_price_from: minPriceFrom,
    has_map_coordinates: latitude !== null && longitude !== null,
    city: item?.city || '',
    address: item?.address || '',
    country: item?.country || '',
    country_code: item?.country_code || '',
    image_url: coverImage,
    cover_image_url: coverImage,
    gallery_images: uniqueGallery.map((image_url, index) => ({
      id: index + 1,
      club_id: toNumber(item?.id || 0, 0),
      image_url,
      alt_text: '',
      sort_order: index,
      is_cover: index === 0 ? 1 : 0,
      is_active: 1,
    })),
    short_description: item?.short_description || '',
    description: item?.description || '',
    description_full: item?.description_full || '',
    work_time_text: item?.work_time_text || '',
    support_phone: item?.support_phone || '',
  };
}

function normalizeSeat(item) {
  return {
    ...item,
    id: toNumber(item?.id || 0, 0),
    club_id: toNumber(item?.club_id || 0, 0),
    sort_order: toNumber(item?.sort_order || 0, 0),
    is_bookable: item?.is_bookable === undefined ? 1 : toNumber(item?.is_bookable || 0, 0),
  };
}

export default {
  namespaced: true,

  state: () => ({
    items: [],
    detailsById: {},
    currentSeats: [],
    loading: false,
    seatsLoading: false,
    error: '',
  }),

  getters: {
    items: (state) => state.items,
    clubs: (state) => state.items,
    loading: (state) => state.loading,
    seatsLoading: (state) => state.seatsLoading,
    error: (state) => state.error,
    hasSelectedClub: (state, getters, rootState) => Boolean(rootState.auth?.user?.club_id),
    currentClub: (state, getters, rootState) => {
      const clubId = toNumber(rootState.auth?.user?.club_id || 0, 0);
      return state.detailsById[clubId] || state.items.find((item) => toNumber(item.id || 0, 0) === clubId) || null;
    },
    clubById: (state) => (clubId) => {
      const id = toNumber(clubId || 0, 0);
      return state.detailsById[id] || state.items.find((item) => toNumber(item.id || 0, 0) === id) || null;
    },
    currentSeats: (state) => state.currentSeats,
    seatsForClub: (state) => () => state.currentSeats,
  },

  mutations: {
    SET_LOADING(state, value) {
      state.loading = value;
    },
    SET_SEATS_LOADING(state, value) {
      state.seatsLoading = value;
    },
    SET_ERROR(state, value) {
      state.error = value;
    },
    SET_ITEMS(state, items) {
      state.items = Array.isArray(items) ? items : [];
    },
    UPSERT_CLUB(state, club) {
      if (!club || !club.id) return;

      state.detailsById = {
        ...state.detailsById,
        [club.id]: club,
      };

      const index = state.items.findIndex((item) => Number(item.id) === Number(club.id));
      if (index === -1) {
        state.items = [...state.items, club];
        return;
      }

      const current = state.items[index];
      const nextClub = {
        ...current,
        ...club,
      };

      if (club.latitude === null && current.latitude !== null && current.latitude !== undefined) {
        nextClub.latitude = current.latitude;
      }

      if (club.longitude === null && current.longitude !== null && current.longitude !== undefined) {
        nextClub.longitude = current.longitude;
      }

      nextClub.has_map_coordinates = nextClub.latitude !== null && nextClub.latitude !== undefined && nextClub.longitude !== null && nextClub.longitude !== undefined;

      state.items.splice(index, 1, nextClub);
    },
    SET_SEATS(state, seats) {
      state.currentSeats = Array.isArray(seats) ? seats : [];
    },
    RESET_STATE(state) {
      state.items = [];
      state.detailsById = {};
      state.currentSeats = [];
      state.loading = false;
      state.seatsLoading = false;
      state.error = '';
    },
  },

  actions: {
    resetState({ commit }) {
      commit('RESET_STATE');
    },

    async fetchClubs({ commit }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', '');

      try {
        const result = await post('/clubs/list', {
          only_active: 1,
        });

        if (!isSuccess(result)) {
          const message = extractMessage(result, 'Не удалось загрузить клубы');
          commit('SET_ERROR', message);
          return [];
        }

        const itemsRaw = Array.isArray(result?.data?.items)
          ? result.data.items
          : Array.isArray(result?.data)
            ? result.data
            : [];

        const items = itemsRaw.map(normalizeClub);
        commit('SET_ITEMS', items);
        items.forEach((item) => commit('UPSERT_CLUB', item));
        return items;
      } catch (error) {
        const message = 'Ошибка сети';
        commit('SET_ERROR', message);
        return [];
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchClubById({ commit }, clubId) {
      const id = toNumber(clubId || 0, 0);
      if (!id) {
        return null;
      }

      commit('SET_ERROR', '');

      try {
        const result = await post('/clubs/get', {
          club_id: id,
        });

        if (!isSuccess(result)) {
          const message = extractMessage(result, 'Не удалось загрузить клуб');
          commit('SET_ERROR', message);
          return null;
        }

        const rawClub = result?.data?.item || result?.data || null;
        if (!rawClub) {
          return null;
        }

        const club = normalizeClub(rawClub);
        commit('UPSERT_CLUB', club);
        return club;
      } catch (error) {
        const message = 'Ошибка сети';
        commit('SET_ERROR', message);
        return null;
      }
    },

    async fetchSeats({ commit }, payload) {
      commit('SET_SEATS_LOADING', true);
      commit('SET_ERROR', '');

      try {
        const clubId = typeof payload === 'object' && payload !== null
          ? toNumber(payload.clubId || payload.club_id || 0, 0)
          : toNumber(payload || 0, 0);

        if (!clubId) {
          commit('SET_SEATS', []);
          return { ok: false, message: 'club_id не передан' };
        }

        const result = await post('/clubs/seats', {
          club_id: clubId,
        });

        if (!isSuccess(result)) {
          const message = extractMessage(result, 'Не удалось загрузить места');
          commit('SET_ERROR', message);
          return { ok: false, message };
        }

        const itemsRaw = Array.isArray(result?.data?.items)
          ? result.data.items
          : Array.isArray(result?.data)
            ? result.data
            : [];

        const items = itemsRaw.map(normalizeSeat);
        commit('SET_SEATS', items);
        return { ok: true, items };
      } catch (error) {
        const message = 'Ошибка сети';
        commit('SET_ERROR', message);
        return { ok: false, message };
      } finally {
        commit('SET_SEATS_LOADING', false);
      }
    },

    async selectClub({ commit, rootState }, clubId) {
      const user = rootState.auth?.user;

      if (!user?.hash) {
        const message = 'Пользователь не авторизован';
        commit('SET_ERROR', message);
        return { ok: false, message };
      }

      const nextClubId = toNumber(clubId || 0, 0);
      const previousUser = { ...user };

      commit('SET_ERROR', '');
      commit(
        'auth/SET_USER',
        {
          ...user,
          club_id: nextClubId,
        },
        { root: true }
      );

      try {
        const result = await post('/user/update-profile-settings', {
          hash: user.hash,
          full_name: user.full_name || '',
          FIO: user.full_name || '',
          phone: user.phone || '',
          club_id: nextClubId,
        });

        if (!isSuccess(result)) {
          const message = extractMessage(result, 'Не удалось выбрать клуб');
          commit('auth/SET_USER', previousUser, { root: true });
          commit('SET_ERROR', message);
          return { ok: false, message };
        }

        commit(
          'auth/SET_USER',
          {
            ...previousUser,
            ...result.data,
            club_id: result?.data?.club_id ?? nextClubId,
          },
          { root: true }
        );

        return { ok: true, club_id: nextClubId };
      } catch (error) {
        const message = 'Ошибка сети';
        commit('auth/SET_USER', previousUser, { root: true });
        commit('SET_ERROR', message);
        return { ok: false, message };
      }
    },
  },
};